import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Email, Refresh } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import type { AdminDialogProps } from '../types/adminTools.types';
import type { TenderEmail } from '../../../types/api';
import { EmailRow } from './tenderEmails/EmailRow';

interface TenderEmailsDialogProps extends AdminDialogProps {
  loading: boolean;
  emails: TenderEmail[];
  count: number;
  error: string;
  expandedId: string | null;
  reprocessingId: string | null;
  statusFilter: string;
  onToggleExpanded: (id: string) => void;
  onReprocess: (id: string) => Promise<void>;
  onStatusFilter: (status: string) => void;
  onRefresh: () => Promise<void>;
}

export const TenderEmailsDialog: React.FC<TenderEmailsDialogProps> = ({
  open,
  onClose,
  loading,
  emails,
  count,
  error,
  expandedId,
  reprocessingId,
  statusFilter,
  onToggleExpanded,
  onReprocess,
  onStatusFilter,
  onRefresh,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="tender-emails-dialog"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email />
            {t('adminToolsPage.tenderEmails.dialogTitle')}
            <Chip label={`${count}`} size="small" color="primary" variant="outlined" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('adminToolsPage.tenderEmails.filterStatus')}</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => onStatusFilter(e.target.value)}
                label={t('adminToolsPage.tenderEmails.filterStatus')}
              >
                <MenuItem value="">{t('adminToolsPage.tenderEmails.allStatuses')}</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={t('adminToolsPage.tenderEmails.refresh')}>
              <IconButton onClick={onRefresh} disabled={loading} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : emails.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('adminToolsPage.tenderEmails.noEmails')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>{t('adminToolsPage.tenderEmails.tableHeaders.subject')}</TableCell>
                    <TableCell>{t('adminToolsPage.tenderEmails.tableHeaders.from')}</TableCell>
                    <TableCell>{t('adminToolsPage.tenderEmails.tableHeaders.status')}</TableCell>
                    <TableCell>
                      {t('adminToolsPage.tenderEmails.tableHeaders.evaluation')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.tenderEmails.tableHeaders.confidence')}
                    </TableCell>
                    <TableCell>{t('adminToolsPage.tenderEmails.tableHeaders.date')}</TableCell>
                    <TableCell align="center">
                      {t('adminToolsPage.tenderEmails.tableHeaders.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emails.map((email) => (
                    <EmailRow
                      key={email.id}
                      email={email}
                      isExpanded={expandedId === email.id}
                      reprocessingId={reprocessingId}
                      onToggleExpanded={onToggleExpanded}
                      onReprocess={onReprocess}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminToolsPage.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
